import type {
  FieldConfig,
  SelectOption,
  SelectOptionValue,
  StepConfig,
  StepFormState,
  StepStatus,
} from './VerticalStepForm.model';
import type { FieldErrors } from 'react-hook-form';
import { _get, _notNil } from '@/littledash';

export const stepHasErrors = (fields: Array<FieldConfig>, errors: FieldErrors): boolean => {
  return fields.some((field) => _notNil(_get(errors, field.name)));
};

const isSelectOption = (value: unknown): value is SelectOption<SelectOptionValue> =>
  typeof value === 'object' &&
  _notNil(value) &&
  'value' in value &&
  (typeof (value as SelectOption<SelectOptionValue>).value === 'string' ||
    typeof (value as SelectOption<SelectOptionValue>).value === 'number');

/**
 * Reduces the editing state to the submit shape: every select field's `{ label, value }` option
 * becomes its bare `value`, leaving non-select fields untouched.
 *
 * The cast is unavoidable rather than lazy — which fields are selects is only known from the runtime
 * `steps` array, whose `name`s are plain `string`s, so the compiler cannot connect a field name to a
 * key of `T`. The `isSelectOption` guard makes the narrowing sound at runtime.
 */
export const flattenSelectValues = <T extends Record<string, unknown>>(
  values: StepFormState<T>,
  steps: Array<StepConfig>
): T => {
  const selectFieldNames = steps.flatMap((step) => step.fields).filter((field) => field.type === 'select');
  return selectFieldNames.reduce<Record<string, unknown>>(
    (result, field) => {
      const current = result[field.name];
      return isSelectOption(current) ? { ...result, [field.name]: current.value } : result;
    },
    { ...values }
  ) as T;
};

export type StepVariant = 'error' | 'completed' | 'in_progress' | 'pending';

export const getStepVariant = (status: StepStatus, hasErrors: boolean): StepVariant => {
  if (hasErrors) {
    return 'error';
  }
  if (status === 'completed') {
    return 'completed';
  }
  if (status === 'in_progress') {
    return 'in_progress';
  }
  return 'pending';
};
